// useVideoTicker.ts
import { useEffect, useRef, useCallback } from "react";
import * as fabric from "fabric";
import { useFrameScheduler } from "./useFrameScheduler";
import { useCanvasRenderer, LayoutData } from "./useCanvasRenderer";
import { RECORDING_FLAGS } from "../constants/recordingFlags";

// Accept a nullable, loosely-typed stage handle.
export type StageHandle = { getCanvas?: () => fabric.Canvas | null } | null;
export type StageRef = React.RefObject<StageHandle> | null;

type Registered = {
  obj: fabric.Object;            // Fabric object (the <video> image)
  el: HTMLVideoElement | null;   // Underlying <video>, if any
  detach?: () => void;           // Unsubscribe for this entry
};

// GLOBAL SHARED REGISTRY for all video ticker instances
// This allows vimeoDirectRender to see ALL registered video elements
// regardless of which hook instance registered them
const globalVideoRegistry = new Set<Registered>();

// Helper to get all globally registered items
function getGlobalVideoItems(): Registered[] {
  return Array.from(globalVideoRegistry);
}

// Helper to add to global registry
function addToGlobalRegistry(item: Registered): void {
  globalVideoRegistry.add(item);
}

// Helper to remove from global registry
function removeFromGlobalRegistry(item: Registered): void {
  globalVideoRegistry.delete(item);
}

// GLOBAL FLAG: Indicates when direct render is actively handling frames
// Used by DraggableResizableStream to skip redundant requestRenderAll() calls
// during hidden tab recording. This prevents double-rendering competition.
let globalDirectRenderActive = false;

// Export getter/setter for global direct render state
export function isDirectRenderActive(): boolean {
  // FLICKER FIX: Return flag directly - the caller (workerTickHandler) now sets
  // the flag when hidden OR recording. The visibility check was preventing
  // Fabric.js suppression during visible-tab recording, causing flicker.
  return globalDirectRenderActive;
}

export function setDirectRenderActive(active: boolean): void {
  globalDirectRenderActive = active;
}

export interface UseVideoTickerOptions {
  onFrameRendered?: (canvas: HTMLCanvasElement, frameNumber: number, renderDurationMs: number) => void;
  shouldCaptureFrame?: () => boolean;
  requestCanvasFrame?: () => boolean; // For captureStream(0) - requests a frame capture
}

export function useVideoTicker(stageRef: StageRef, options: UseVideoTickerOptions = {}) {
  const { onFrameRendered, shouldCaptureFrame, requestCanvasFrame } = options;
  const rafRef = useRef<number | null>(null);
  const runningRef = useRef(false);
  const canvasRef = useRef<fabric.Canvas | null>(null);
  const prevCanvasRef = useRef<fabric.Canvas | null>(null);

  const itemsRef = useRef<Set<Registered>>(new Set());

  // Governing (legacy RAF-based hidden tab timing)
  const hiddenTargetFps = 24;
  const hiddenMinGapMs = Math.floor(1000 / hiddenTargetFps);
  const lastHiddenPaintRef = useRef(0);
  const needHiddenPaintRef = useRef(false);
  
  // Phase 4: Throttle VISIBLE tab renders to 30fps to match recording target
  // Prevents 60fps video callbacks from overwhelming canvas.renderAll()
  const visibleTargetFps = 30;
  const visibleMinGapMs = Math.floor(1000 / visibleTargetFps); // ~33ms
  const lastVisiblePaintRef = useRef(0);

  // Coalescing via Fabric lifecycle
  const isRenderingRef = useRef(false);
  const pendingRef = useRef(false);

  // Track Worker running state via ref to avoid effect dependency cycles
  const isWorkerRunningRef = useRef(false);
  const isOffscreenActiveRef = useRef(false);
  
  // Ref for onFrameRendered to avoid dependency issues
  const onFrameRenderedRef = useRef(onFrameRendered);
  onFrameRenderedRef.current = onFrameRendered;
  
  // Ref for shouldCaptureFrame to avoid dependency issues
  const shouldCaptureFrameRef = useRef(shouldCaptureFrame);
  shouldCaptureFrameRef.current = shouldCaptureFrame;
  
  // Ref for requestCanvasFrame to avoid dependency issues
  const requestCanvasFrameRef = useRef(requestCanvasFrame);
  requestCanvasFrameRef.current = requestCanvasFrame;
  
  // Track frame number for MSTG (increments only during active capture)
  const frameCountRef = useRef(0);
  
  // Track render timing for MSTG frame budget
  const renderStartTimeRef = useRef(0);
  
  // MSTG writer ref for OffscreenCanvas integration
  const mstgWriterRef = useRef<WritableStreamDefaultWriter<VideoFrame> | null>(null);
  const mstgTrackRef = useRef<MediaStreamTrackGenerator<VideoFrame> | null>(null);

  // OffscreenCanvas-based rendering for hidden tab (Phase 5)
  const FRAME_DURATION_US = 33_333;
  
  // Track if we've received at least one frame from offscreen worker for watchdog
  const offscreenFrameReceivedRef = useRef(false);
  const offscreenWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track if we should start offscreen worker after initialization
  const pendingOffscreenStartRef = useRef(false);
  // Store canvas ref for onInitialized callback
  const pendingOffscreenCanvasRef = useRef<fabric.Canvas | null>(null);
  
  const handleOffscreenFrame = useCallback((bitmap: ImageBitmap, frameNumber: number, timestamp: number) => {
    // Mark that we've received a frame - worker is working
    offscreenFrameReceivedRef.current = true;
    
    const writer = mstgWriterRef.current;
    const track = mstgTrackRef.current;
    
    if (!writer || !track || track.readyState !== 'live') {
      bitmap.close();
      return;
    }
    
    try {
      const frame = new VideoFrame(bitmap, {
        timestamp,
        duration: FRAME_DURATION_US,
        alpha: 'discard',
      });
      
      writer.write(frame).then(() => frame.close()).catch(() => {
        try { frame.close(); } catch {}
      });
    } catch (error) {
      if (!(error instanceof Error && error.message.includes('Stream closed'))) {
        console.error('[useVideoTicker] OffscreenCanvas frame write error:', error);
      }
    } finally {
      try { bitmap.close(); } catch {}
    }
  }, []);
  
  // Handler for when OffscreenCanvas worker is initialized
  const handleOffscreenInitialized = useCallback(() => {
    if (!pendingOffscreenStartRef.current) return;
    pendingOffscreenStartRef.current = false;
    
    // Start the worker now that it's initialized
    startOffscreenRef.current?.();
    
    const canvas = pendingOffscreenCanvasRef.current;
    if (RECORDING_FLAGS.TELEMETRY_ENABLED) {
      console.log('[VisibilityBridge] Started OffscreenCanvas worker for hidden-tab recording (on init)');
    }
    
    // WATCHDOG: If no frames received within 100ms (3 frame intervals), permanently disable worker
    if (offscreenWatchdogRef.current) {
      clearTimeout(offscreenWatchdogRef.current);
    }
    offscreenWatchdogRef.current = setTimeout(() => {
      if (!offscreenFrameReceivedRef.current && document.visibilityState === 'hidden') {
        console.warn('[VisibilityBridge] WATCHDOG: OffscreenCanvas worker not producing frames, disabling for this session');
        offscreenDisabledRef.current = true;
        stopOffscreenRef.current?.();
        if (canvas) {
          canvas.renderAll();
        }
        if (RECORDING_FLAGS.TELEMETRY_ENABLED) {
          console.log('[VisibilityBridge] OffscreenCanvas disabled, resuming Fabric-only rendering for hidden tab');
        }
      }
    }, 100);
  }, []);
  
  const {
    init: initOffscreen,
    start: startOffscreen,
    stop: stopOffscreen,
    updateLayout: updateOffscreenLayout,
    updateWebcamFrame: updateOffscreenWebcam,
    updateScreenFrame: updateOffscreenScreen,
    isRunning: isOffscreenRunning,
    workerEnabled: offscreenEnabled,
  } = useCanvasRenderer({
    fps: 30,
    onFrame: handleOffscreenFrame,
    onInitialized: handleOffscreenInitialized,
  });
  
  // Track if the OffscreenCanvas worker has been disabled due to failure (for this session)
  const offscreenDisabledRef = useRef(false);
  
  // Vimeo-style direct render: track success/failure for fallback
  const vimeoDirectRenderFailCountRef = useRef(0);
  const vimeoDirectRenderSuccessCountRef = useRef(0);
  const vimeoRenderCountRef = useRef(0); // For throttling debug logs
  const VIMEO_FALLBACK_THRESHOLD = 30; // If 30+ consecutive failures, fall back
  
  // Flag to skip Fabric.js after:render frame capture when direct render is handling frames
  // This prevents double frame submission to MSTG
  const directRenderActiveRef = useRef(false);
  
  // Performance telemetry (aggregated per-second)
  const telemetryRef = useRef({
    windowStart: 0,
    framesRendered: 0,
    framesCaptured: 0,
    totalRenderMs: 0,
    maxRenderMs: 0,
    droppedFrames: 0,
    driver: 'none' as 'fabric' | 'direct' | 'worker' | 'none',
  });
  
  // Log aggregated telemetry every second (when TELEMETRY_ENABLED)
  const logTelemetry = useCallback(() => {
    if (!RECORDING_FLAGS.TELEMETRY_ENABLED) return;
    
    const t = telemetryRef.current;
    const now = performance.now();
    
    // Initialize window start if not set
    if (t.windowStart === 0) {
      t.windowStart = now;
      return;
    }
    
    const elapsed = now - t.windowStart;
    
    if (elapsed >= 1000 && t.framesRendered > 0) {
      
      // Reset for next window
      t.windowStart = now;
      t.framesRendered = 0;
      t.framesCaptured = 0;
      t.totalRenderMs = 0;
      t.maxRenderMs = 0;
      t.droppedFrames = 0;
    }
  }, []);
  
  // Keep offscreen active ref in sync - only true when worker is running AND tab is hidden
  // The worker's `started` signal is now the gate, not frame receipt (to avoid double-render thrash)
  isOffscreenActiveRef.current = isOffscreenRunning && document.visibilityState === 'hidden' && !offscreenDisabledRef.current;
  
  /**
   * Helper to draw a video element using SCREEN COORDINATES directly
   * 
   * Key insight: clipPath dimensions are in SCREEN SPACE (already account for scale)
   * So we compute final screen position/size and draw directly without transform matrix
   * 
   * This avoids the complexity of Fabric's transform system and produces 1:1 results
   */
  const drawVideoWithFabricTransform = useCallback((
    ctx: CanvasRenderingContext2D,
    obj: fabric.Object,
    el: HTMLVideoElement,
    options?: { isCircle?: boolean; shadow?: fabric.Shadow | null; clipPath?: fabric.Object | null; borderRadius?: number }
  ): boolean => {
    if (el.readyState < 2 || el.videoWidth === 0 || el.videoHeight === 0) {
      return false; // Video not ready
    }
    
    const imgObj = obj as any;
    const clipPath = options?.clipPath || imgObj.clipPath;
    
    // Get object properties
    const objWidth = obj.width ?? el.videoWidth;
    const objHeight = obj.height ?? el.videoHeight;
    const scaleX = obj.scaleX ?? 1;
    const scaleY = obj.scaleY ?? 1;
    const left = obj.left ?? 0;
    const top = obj.top ?? 0;
    const originX = (obj as any).originX ?? 'center';
    const originY = (obj as any).originY ?? 'center';
    const flipX = obj.flipX ?? false;
    
    // Calculate FINAL screen dimensions
    const finalWidth = objWidth * scaleX;
    const finalHeight = objHeight * scaleY;
    
    // Calculate FINAL screen position (top-left corner)
    let screenX = left;
    let screenY = top;
    if (originX === 'center') screenX -= finalWidth / 2;
    else if (originX === 'right') screenX -= finalWidth;
    if (originY === 'center') screenY -= finalHeight / 2;
    else if (originY === 'bottom') screenY -= finalHeight;
    
    // ClipPath dimensions are in SCREEN SPACE
    const clipW = clipPath?.width ?? finalWidth;
    const clipH = clipPath?.height ?? finalHeight;
    const borderRadius = options?.borderRadius || (clipPath as any)?.rx || (clipPath as any)?.ry || 0;
    const isCircle = options?.isCircle || clipPath?.type === 'circle';
    
    // Calculate clip position (clipPath.left/top are screen coordinates)
    let clipX = clipPath?.left ?? left;
    let clipY = clipPath?.top ?? top;
    // Adjust for center origin (clipPath is also centered)
    clipX -= clipW / 2;
    clipY -= clipH / 2;
    
    ctx.save();
    
    // Helper to draw the clip shape path
    const drawClipPath = () => {
      ctx.beginPath();
      if (isCircle) {
        const radius = (clipPath as any)?.radius ?? Math.min(clipW, clipH) / 2;
        ctx.arc(clipX + clipW / 2, clipY + clipH / 2, radius, 0, Math.PI * 2);
      } else if (borderRadius > 0) {
        const r = Math.min(borderRadius, clipW / 2, clipH / 2);
        ctx.moveTo(clipX + r, clipY);
        ctx.lineTo(clipX + clipW - r, clipY);
        ctx.quadraticCurveTo(clipX + clipW, clipY, clipX + clipW, clipY + r);
        ctx.lineTo(clipX + clipW, clipY + clipH - r);
        ctx.quadraticCurveTo(clipX + clipW, clipY + clipH, clipX + clipW - r, clipY + clipH);
        ctx.lineTo(clipX + r, clipY + clipH);
        ctx.quadraticCurveTo(clipX, clipY + clipH, clipX, clipY + clipH - r);
        ctx.lineTo(clipX, clipY + r);
        ctx.quadraticCurveTo(clipX, clipY, clipX + r, clipY);
      } else {
        ctx.rect(clipX, clipY, clipW, clipH);
      }
      ctx.closePath();
    };
    
    // Step 1: Draw shadow on the CLIPPED shape (before clipping the video)
    // Canvas 2D shadows require drawing a filled shape - we draw offscreen and let shadow appear
    const shadow = options?.shadow || imgObj.shadow;
    if (shadow && (clipPath || borderRadius > 0 || isCircle)) {
      ctx.save();
      
      // Get shadow properties (handle both fabric.Shadow and plain objects)
      const shadowColor = shadow.color ?? 'rgba(0,0,0,0.28)';
      const shadowBlur = shadow.blur ?? 18;
      const shadowOffsetX = shadow.offsetX ?? 2;
      const shadowOffsetY = shadow.offsetY ?? 4;
      
      ctx.shadowColor = shadowColor;
      ctx.shadowBlur = shadowBlur;
      ctx.shadowOffsetX = shadowOffsetX;
      ctx.shadowOffsetY = shadowOffsetY;
      
      // Draw the shape offscreen (negative translate) so only shadow is visible
      // This ensures shadow appears around the clip shape without the shape itself showing
      const offsetDistance = Math.max(clipW, clipH) * 2;
      ctx.translate(0, -offsetDistance);
      
      // Draw filled shape - the shadow will appear at the original position due to offset
      ctx.beginPath();
      if (isCircle) {
        const radius = (clipPath as any)?.radius ?? Math.min(clipW, clipH) / 2;
        ctx.arc(clipX + clipW / 2, clipY + clipH / 2 + offsetDistance, radius, 0, Math.PI * 2);
      } else if (borderRadius > 0) {
        const r = Math.min(borderRadius, clipW / 2, clipH / 2);
        const cy = clipY + offsetDistance; // Adjusted Y position
        ctx.moveTo(clipX + r, cy);
        ctx.lineTo(clipX + clipW - r, cy);
        ctx.quadraticCurveTo(clipX + clipW, cy, clipX + clipW, cy + r);
        ctx.lineTo(clipX + clipW, cy + clipH - r);
        ctx.quadraticCurveTo(clipX + clipW, cy + clipH, clipX + clipW - r, cy + clipH);
        ctx.lineTo(clipX + r, cy + clipH);
        ctx.quadraticCurveTo(clipX, cy + clipH, clipX, cy + clipH - r);
        ctx.lineTo(clipX, cy + r);
        ctx.quadraticCurveTo(clipX, cy, clipX + r, cy);
      } else {
        ctx.rect(clipX, clipY + offsetDistance, clipW, clipH);
      }
      ctx.closePath();
      ctx.fillStyle = '#000000'; // Solid fill for proper shadow generation
      ctx.fill();
      
      ctx.restore();
    }
    
    // Step 2: Apply clipping in SCREEN coordinates
    if (clipPath || borderRadius > 0 || isCircle) {
      drawClipPath();
      ctx.clip();
    }
    
    // Step 3: Handle flipX (mirrored webcam)
    if (flipX) {
      ctx.translate(screenX + finalWidth / 2, 0);
      ctx.scale(-1, 1);
      ctx.translate(-(screenX + finalWidth / 2), 0);
    }
    
    // Step 4: Draw video directly to SCREEN coordinates
    // Source: full video (or cropped if cropX/cropY set)
    const videoCropX = Math.max(imgObj.cropX ?? 0, 0);
    const videoCropY = Math.max(imgObj.cropY ?? 0, 0);
    ctx.drawImage(
      el,
      videoCropX, videoCropY, objWidth, objHeight,  // Source rect
      screenX, screenY, finalWidth, finalHeight  // Dest rect in screen coords
    );
    
    ctx.restore();
    return true;
  }, []);

  /**
   * Vimeo-style Direct Canvas 2D Render
   * 
   * Bypasses Fabric.js entirely and uses raw Canvas 2D API (drawImage, fillRect).
   * Canvas drawing operations are NOT throttled by the browser when tab is hidden,
   * unlike requestAnimationFrame which Fabric.js uses internally.
   * 
   * Uses Fabric.js calcTransformMatrix() to properly handle origin, scale, rotation.
   * This ensures pixel-perfect alignment with Fabric.js rendering.
   * 
   * @param skipClear - If true, skip clearRect (use when visible to prevent flicker)
   * @returns true if render succeeded, false if it failed
   */
  const vimeoDirectRender = useCallback((skipClear: boolean = false): boolean => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    
    const domCanvas = (canvas as any).lowerCanvasEl as HTMLCanvasElement | undefined;
    if (!domCanvas) return false;
    
    const ctx = domCanvas.getContext('2d');
    if (!ctx) return false;
    
    const width = domCanvas.width;
    const height = domCanvas.height;
    
    // Validate canvas dimensions
    if (width <= 0 || height <= 0) {
      return false;
    }
    
    try {
      // Step 1: Clear canvas (skip when visible to prevent flicker)
      // When visible, background fill will cover everything anyway
      if (!skipClear) {
        ctx.clearRect(0, 0, width, height);
      }
      
      // Step 2: Draw background
      // The background can be: 
      // a) canvas.backgroundColor (string color or gradient object)
      // b) A fabric.Rect object at the back of the canvas with gradient fill (CanvasBackground.tsx pattern)
      const bg = canvas.backgroundColor;
      let backgroundDrawn = false;
      
      // First, check for background Rects in canvas objects (CanvasBackground.tsx pattern)
      // May include a black base rect (name="background-base") + gradient rect (name="background")
      const objects = canvas.getObjects();
      for (const obj of objects) {
        if (obj.type === 'rect' && 
            obj.left === 0 && obj.top === 0 && 
            obj.width === width && obj.height === height) {
          const objName = (obj as any).name;
          const fill = (obj as any).fill;

          if (objName === 'background-base' && typeof fill === 'string') {
            ctx.fillStyle = fill;
            ctx.fillRect(0, 0, width, height);
            backgroundDrawn = true;
            continue;
          }

          if (fill && typeof fill === 'object' && 'colorStops' in fill) {
            const grad = fill as any;
            const stops = grad.colorStops || [];
            const coords = grad.coords || {};
            
            let canvasGrad: CanvasGradient;
            if (grad.type === 'linear') {
              const x1 = (coords.x1 ?? 0) * width;
              const y1 = (coords.y1 ?? 0) * height;
              const x2 = (coords.x2 ?? 1) * width;
              const y2 = (coords.y2 ?? 1) * height;
              canvasGrad = ctx.createLinearGradient(x1, y1, x2, y2);
            } else {
              const cx = (coords.x1 ?? 0.5) * width;
              const cy = (coords.y1 ?? 0.5) * height;
              const r = (coords.r1 ?? 1) * Math.max(width, height);
              canvasGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
            }
            
            stops.forEach((stop: any) => {
              canvasGrad.addColorStop(stop.offset, stop.color);
            });
            
            ctx.fillStyle = canvasGrad;
            ctx.fillRect(0, 0, width, height);
            backgroundDrawn = true;
            break;
          } else if (typeof fill === 'string' && fill !== 'transparent') {
            ctx.fillStyle = fill;
            ctx.fillRect(0, 0, width, height);
            backgroundDrawn = true;
            break;
          }
        }
      }
      
      // Fallback: Check canvas.backgroundColor
      if (!backgroundDrawn) {
        if (typeof bg === 'string' && bg !== 'transparent') {
          ctx.fillStyle = bg;
          ctx.fillRect(0, 0, width, height);
          backgroundDrawn = true;
        } else if (bg && typeof bg === 'object' && 'colorStops' in bg) {
          // Handle gradient background from canvas.backgroundColor
          const grad = bg as any;
          const stops = grad.colorStops || [];
          
          let canvasGrad: CanvasGradient;
          if (grad.type === 'linear') {
            const coords = grad.coords || {};
            const x1 = coords.x1 ?? 0;
            const y1 = coords.y1 ?? 0;
            const x2 = coords.x2 ?? width;
            const y2 = coords.y2 ?? height;
            canvasGrad = ctx.createLinearGradient(x1, y1, x2, y2);
          } else {
            const coords = grad.coords || {};
            const cx = coords.x1 ?? width / 2;
            const cy = coords.y1 ?? height / 2;
            const r = coords.r1 ?? Math.max(width, height);
            canvasGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
          }
          
          stops.forEach((stop: any) => {
            canvasGrad.addColorStop(stop.offset, stop.color);
          });
          
          ctx.fillStyle = canvasGrad;
          ctx.fillRect(0, 0, width, height);
          backgroundDrawn = true;
        }
      }
      
      // Check for background IMAGE (fabric.Image with name="background")
      // This handles CanvasBackground.tsx pattern where background is a fabric.Image
      if (!backgroundDrawn) {
        for (const obj of objects) {
          if (obj.type === 'image' && (obj as any).name === 'background' && obj.visible) {
            const imgElement = (obj as any).getElement?.() ?? (obj as any)._element;
            if (imgElement && (imgElement.tagName === 'IMG' || imgElement instanceof HTMLImageElement)) {
              try {
                const imgObj = obj as fabric.Image;
                const scaleX = imgObj.scaleX ?? 1;
                const scaleY = imgObj.scaleY ?? 1;
                const imgWidth = (imgObj.width ?? imgElement.width) * scaleX;
                const imgHeight = (imgObj.height ?? imgElement.height) * scaleY;
                const left = imgObj.left ?? 0;
                const top = imgObj.top ?? 0;
                
                ctx.drawImage(imgElement, left, top, imgWidth, imgHeight);
                backgroundDrawn = true;
                break;
              } catch (e) {
                // Image draw failed, continue to fallback
              }
            }
          }
        }
      }
      
      // Last resort: draw a default gray background instead of black
      if (!backgroundDrawn) {
        ctx.fillStyle = '#1F2937'; // Default dark gray fallback
        ctx.fillRect(0, 0, width, height);
      }
      
      // Step 3: Scan canvas objects directly for video elements
      // This is more reliable than the global registry when components mount at different times
      // Order: screen first (background), then webcam (foreground)
      const canvasObjects = canvas.getObjects();
      
      // Find screen and webcam video objects directly from canvas
      let screenObj: fabric.Object | null = null;
      let screenEl: HTMLVideoElement | null = null;
      let webcamObj: fabric.Object | null = null;
      let webcamEl: HTMLVideoElement | null = null;
      
      for (const obj of canvasObjects) {
        const name = (obj as any).name;
        if (name === 'screen-video' && obj.visible) {
          screenObj = obj;
          // Get video element from fabric.Image object
          const element = (obj as any).getElement?.() ?? (obj as any)._element;
          if (element && element.tagName === 'VIDEO') {
            screenEl = element as HTMLVideoElement;
          }
        }
        if (name === 'webcam-video' && obj.visible) {
          webcamObj = obj;
          const element = (obj as any).getElement?.() ?? (obj as any)._element;
          if (element && element.tagName === 'VIDEO') {
            webcamEl = element as HTMLVideoElement;
          }
        }
      }
      
      // Increment render count
      vimeoRenderCountRef.current++;
      
      // Check if we have any video to render
      const hasVideoToRender = (screenObj && screenEl) || (webcamObj && webcamEl);
      if (!hasVideoToRender) {
        // No video objects found - trigger fallback to Fabric.js
        return false;
      }
      
      // Track render success - if any expected video fails to render, trigger fallback
      let renderSuccess = true;
      
      // Draw screen video first (if present)
      if (screenObj && screenEl) {
        const screenClipPath = (screenObj as any).clipPath as fabric.Object | null;
        const screenShadow = screenObj.shadow as fabric.Shadow | null;
        const screenBorderRadius = (screenClipPath as any)?.rx || (screenClipPath as any)?.ry || 0;
        
        const screenRendered = drawVideoWithFabricTransform(ctx, screenObj, screenEl, {
          shadow: screenShadow,
          clipPath: screenClipPath,
          borderRadius: screenBorderRadius
        });
        if (!screenRendered) {
          if (RECORDING_FLAGS.VIMEO_TELEMETRY) {
            console.warn('[VimeoDirectRender] Screen video failed to render (not ready or invalid dimensions)');
          }
          renderSuccess = false;
        }
      }
      
      // Draw webcam video (if present)
      if (webcamObj && webcamEl) {
        const obj = webcamObj;
        
        // Check if webcam has circular clip path
        // We use multiple detection methods since constructor.name can be minified in production
        const clipPath = (obj as any).clipPath as fabric.Object | null;
        const isCircle = clipPath && (
          clipPath.type === 'circle' ||                        // Fabric type property (most reliable)
          typeof (clipPath as any).radius === 'number' ||      // Has radius property = circle
          (clipPath instanceof fabric.Circle) ||               // instanceof check
          clipPath.constructor?.name === 'Circle'              // Constructor name (may fail with minification)
        );
        
        // Get shadow
        const shadow = obj.shadow as fabric.Shadow | null;
        
        // Get border radius from clipPath (Rect uses rx/ry, Circle uses radius)
        const borderRadius = (clipPath as any)?.rx || (clipPath as any)?.ry || (clipPath as any)?.radius || 0;
        
        // Pass clipPath to properly constrain the webcam to its container
        const webcamRendered = drawVideoWithFabricTransform(ctx, obj, webcamEl, { 
          isCircle: isCircle || undefined, 
          shadow,
          clipPath,
          borderRadius
        });
        if (!webcamRendered) {
          if (RECORDING_FLAGS.VIMEO_TELEMETRY) {
            console.warn('[VimeoDirectRender] Webcam video failed to render (not ready or invalid dimensions)');
          }
          renderSuccess = false;
        }
      }
      
      return renderSuccess;
    } catch (error) {
      if (RECORDING_FLAGS.VIMEO_TELEMETRY) {
        console.error('[VimeoDirectRender] Error during render:', error);
      }
      return false;
    }
  }, [drawVideoWithFabricTransform]);
  
  // Keep ref in sync for visibility handler closure
  const isOffscreenRunningRef = useRef(isOffscreenRunning);
  isOffscreenRunningRef.current = isOffscreenRunning;

  // Worker-based frame scheduling (for unthrottled hidden tab rendering)
  // Only activates when RECORDING_FLAGS.WORKER_SCHEDULER is enabled
  
  // Diagnostic: Track tick processing rate
  const ticksReceivedRef = useRef(0);
  const ticksProcessedRef = useRef(0);
  const lastTickDiagnosticRef = useRef(0);
  
  const workerTickHandler = useCallback((frameNumber: number, _timestamp: number) => {
    const canvas = canvasRef.current;
    
    ticksReceivedRef.current++;
    
    if (!canvas) {
      // Log every 100th skip
      if (ticksReceivedRef.current % 100 === 0) {
        console.warn(`[WorkerTick] Skipped ${ticksReceivedRef.current} ticks - no canvas`);
      }
      return;
    }
    
    const isHidden = document.visibilityState === 'hidden';
    
    // Check if we should always process ticks (when capturing/recording)
    // This ensures consistent frame rate regardless of visibility toggling
    const isCapturing = shouldCaptureFrameRef.current?.() ?? false;
    
    // Only skip worker ticks when:
    // 1. Tab is visible AND
    // 2. We're NOT actively capturing/recording
    // When capturing, always process ticks to maintain consistent FPS
    if (!isHidden && !isCapturing) {
      return;
    }
    
    ticksProcessedRef.current++;
    
    // Initialize telemetry window if needed
    if (telemetryRef.current.windowStart === 0) {
      telemetryRef.current.windowStart = performance.now();
    }
    
    // VIMEO-STYLE DIRECT RENDER: Use for both visible and hidden tabs
    // Canvas 2D API (drawImage, fillRect) is NOT throttled when hidden, unlike requestAnimationFrame
    if (RECORDING_FLAGS.VIMEO_DIRECT_RENDER && 
        vimeoDirectRenderFailCountRef.current < VIMEO_FALLBACK_THRESHOLD) {
      
      // FLICKER FIX: When recording (isCapturing=true), ALWAYS suppress Fabric.js
      // by setting the global directRenderActive flag.
      // This prevents competition between direct render and Fabric's own render loop.
      // 
      // When NOT recording but visible: don't suppress Fabric (normal preview mode)
      // When recording (visible or hidden): suppress Fabric, use only direct render
      //
      // skipClear=true when visible prevents the clearRect blank frame issue.
      directRenderActiveRef.current = true;
      if (isHidden || isCapturing) {
        setDirectRenderActive(true);  // Suppress Fabric when hidden OR recording
      }
      telemetryRef.current.driver = 'direct';
      
      const renderStart = performance.now();
      // FLICKER FIX: Skip clearRect when visible - background fill covers everything anyway
      // clearRect causes momentary blank canvas that shows as flicker
      const success = vimeoDirectRender(!isHidden);  // skipClear=true when visible
      const renderDuration = performance.now() - renderStart;
      
      // Update telemetry
      telemetryRef.current.framesRendered++;
      telemetryRef.current.totalRenderMs += renderDuration;
      telemetryRef.current.maxRenderMs = Math.max(telemetryRef.current.maxRenderMs, renderDuration);
      
      if (success) {
        vimeoDirectRenderSuccessCountRef.current++;
        vimeoDirectRenderFailCountRef.current = 0; // Reset fail count on success
        
        // Track frame capture for telemetry
        // Note: captureStream(30) handles frame capture automatically - no manual requestFrame()
        // This avoids event loop flooding when switching back from hidden to visible tab
        const shouldCapture = shouldCaptureFrameRef.current?.() ?? false;
        if (shouldCapture) {
          const domCanvas = (canvas as any).lowerCanvasEl as HTMLCanvasElement;
          if (domCanvas) {
            const currentFrame = frameCountRef.current;
            frameCountRef.current++;
            telemetryRef.current.framesCaptured++;
            
            // MSTG path: call onFrameRendered if MSTG is enabled
            // For captureStream(30), the browser handles capture automatically
            if (onFrameRenderedRef.current) {
              Promise.resolve(onFrameRenderedRef.current(domCanvas, currentFrame, renderDuration))
                .catch(() => {/* Silently swallow - writeFrame handles its own errors */});
            }
          }
        }
        
        // Log aggregated telemetry
        logTelemetry();
        
        return; // Successfully rendered, skip Fabric.js
      } else {
        vimeoDirectRenderFailCountRef.current++;
        telemetryRef.current.droppedFrames++;
        
        // Reset flags on failure - will fall through to Fabric.js below
        directRenderActiveRef.current = false;
        if (isHidden || isCapturing) {
          setDirectRenderActive(false);  // Reset global flag when hidden OR recording
        }
        
        // Check if we should trigger permanent fallback
        if (vimeoDirectRenderFailCountRef.current >= VIMEO_FALLBACK_THRESHOLD) {
          console.warn('[VimeoDirectRender] Too many failures, falling back to Fabric.js renderAll()');
        }
      }
    }
    
    // Fallback: Sync render triggered by Worker via Fabric.js
    // Only use when direct render is disabled or failing
    directRenderActiveRef.current = false;
    if (isHidden) {
      setDirectRenderActive(false);  // Only reset global flag when hidden
    }
    telemetryRef.current.driver = 'fabric';
    
    canvas.renderAll();
    
    // Log aggregated telemetry
    logTelemetry();
  }, [vimeoDirectRender, logTelemetry]);

  const { start: startWorker, stop: stopWorker, isRunning: isWorkerRunning, workerEnabled } = useFrameScheduler({
    fps: 30, // Target 30 FPS for recording
    onTick: workerTickHandler,
  });

  // Keep refs in sync with state/functions (to avoid effect dependency cycles)
  // These refs are updated on every render so cleanup always has fresh references
  isWorkerRunningRef.current = isWorkerRunning;
  
  // Use refs for start/stop functions to avoid effect re-runs
  const startWorkerRef = useRef<typeof startWorker | null>(null);
  const stopWorkerRef = useRef<typeof stopWorker | null>(null);
  startWorkerRef.current = startWorker;
  stopWorkerRef.current = stopWorker;
  
  // Refs for offscreen canvas worker functions
  const initOffscreenRef = useRef(initOffscreen);
  const startOffscreenRef = useRef(startOffscreen);
  const stopOffscreenRef = useRef(stopOffscreen);
  const updateOffscreenLayoutRef = useRef(updateOffscreenLayout);
  const updateOffscreenWebcamRef = useRef(updateOffscreenWebcam);
  const updateOffscreenScreenRef = useRef(updateOffscreenScreen);
  initOffscreenRef.current = initOffscreen;
  startOffscreenRef.current = startOffscreen;
  stopOffscreenRef.current = stopOffscreen;
  updateOffscreenLayoutRef.current = updateOffscreenLayout;
  updateOffscreenWebcamRef.current = updateOffscreenWebcam;
  updateOffscreenScreenRef.current = updateOffscreenScreen;
  
  // Helper to extract layout from Fabric.js canvas for OffscreenCanvas worker
  const extractLayoutFromCanvas = useCallback((canvas: fabric.Canvas): LayoutData => {
    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();
    
    let backgroundColor: string | null = null;
    let backgroundGradient: LayoutData['backgroundGradient'] = null;
    
    const bg = canvas.backgroundColor;
    if (typeof bg === 'string') {
      backgroundColor = bg;
    } else if (bg && typeof bg === 'object' && 'colorStops' in bg) {
      const grad = bg as fabric.Gradient<'linear' | 'radial'>;
      const stops = ((grad as any).colorStops || []).map((stop: any) => ({
        offset: stop.offset,
        color: stop.color,
      }));
      
      if (grad.type === 'linear') {
        const coords = grad.coords as fabric.GradientCoords<'linear'>;
        const x1 = coords?.x1 ?? 0;
        const y1 = coords?.y1 ?? 0;
        const x2 = coords?.x2 ?? canvasWidth;
        const y2 = coords?.y2 ?? canvasHeight;
        const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI) + 90;
        backgroundGradient = { type: 'linear', angle, stops };
      } else {
        const coords = grad.coords as fabric.GradientCoords<'radial'>;
        backgroundGradient = {
          type: 'radial',
          cx: coords?.x1 ?? canvasWidth / 2,
          cy: coords?.y1 ?? canvasHeight / 2,
          r: coords?.r1 ?? Math.max(canvasWidth, canvasHeight),
          stops,
        };
      }
    }
    
    let webcam: LayoutData['webcam'] = null;
    let screen: LayoutData['screen'] = null;
    
    const objects = canvas.getObjects();
    
    for (const obj of objects) {
      const name = (obj as any).name;
      
      if (name === 'webcam-video') {
        const shadow = obj.shadow as fabric.Shadow | null;
        const clipPath = (obj as any).clipPath;
        const isCircle = clipPath instanceof fabric.Circle;
        
        webcam = {
          left: obj.left ?? 0,
          top: obj.top ?? 0,
          width: (obj.width ?? 0) * (obj.scaleX ?? 1),
          height: (obj.height ?? 0) * (obj.scaleY ?? 1),
          flipX: obj.flipX ?? false,
          isCircle,
          shadowColor: shadow?.color ?? 'rgba(0,0,0,0.28)',
          shadowBlur: shadow?.blur ?? 18,
          shadowOffsetX: shadow?.offsetX ?? 2,
          shadowOffsetY: shadow?.offsetY ?? 4,
        };
      }
      
      if (name === 'screen-video') {
        screen = {
          left: obj.left ?? 0,
          top: obj.top ?? 0,
          width: (obj.width ?? 0) * (obj.scaleX ?? 1),
          height: (obj.height ?? 0) * (obj.scaleY ?? 1),
        };
      }
    }
    
    return {
      canvasWidth,
      canvasHeight,
      backgroundColor,
      backgroundGradient,
      webcam,
      screen,
    };
  }, []);
  
  // Helper to capture video frame as ImageBitmap
  const captureVideoFrame = useCallback(async (el: HTMLVideoElement): Promise<ImageBitmap | null> => {
    try {
      if (el.readyState < 2 || el.videoWidth === 0 || el.videoHeight === 0) return null;
      return await createImageBitmap(el);
    } catch {
      return null;
    }
  }, []);

  const markDirtyAndRender = useCallback((canvas: fabric.Canvas, obj?: fabric.Object) => {
    if (obj) (obj as any).dirty = true;

    const hidden = document.visibilityState === 'hidden';
    const now = performance.now();
    
    // Check if we're actively recording (worker is driving frame capture)
    const isRecordingActive = shouldCaptureFrameRef.current?.() ?? false;
    
    // Skip Fabric.js renders when worker is driving frame capture during recording
    // This prevents double-rendering flickering between direct render and Fabric.js
    // Applies both when hidden AND when visible during recording
    if (isRecordingActive && workerEnabled && isWorkerRunningRef.current) {
      return;
    }

    if (!hidden) {
      // Phase 4: Throttle VISIBLE tab renders to 30fps (preview only, not during recording)
      // This prevents 60fps video callbacks from overwhelming canvas.renderAll()
      // which was causing 50-175ms render times and frame duplication
      if (now - lastVisiblePaintRef.current < visibleMinGapMs) {
        // Too soon since last paint - skip, fabric will coalesce
        return;
      }
      
      // Visible: non-blocking coalesced paint (preview mode only)
      lastVisiblePaintRef.current = now;
      canvas.requestRenderAll?.();
      return;
    }

    // Hidden tab rendering:
    // If Worker scheduler is enabled AND actually running, defer to worker
    // Must check isWorkerRunningRef to avoid freezing if worker fails to start
    if (workerEnabled && isWorkerRunningRef.current) {
      // Worker will call canvas.renderAll() on its tick schedule
      return;
    }

    // Legacy path: governed + coalesced synchronous paint
    // Only used when WORKER_SCHEDULER flag is disabled
    needHiddenPaintRef.current = true;

    if (now - lastHiddenPaintRef.current < hiddenMinGapMs) {
      // too soon, let the next frame/event trigger it
      return;
    }

    if (isRenderingRef.current) {
      pendingRef.current = true;
      return;
    }

    needHiddenPaintRef.current = false;
    canvas.renderAll(); // one sync paint
    lastHiddenPaintRef.current = now;
  }, [workerEnabled]);

  // Attach per-video frame callbacks & events
  const attachVideoHooks = useCallback(
    (entry: Registered) => {
      const canvas = canvasRef.current;
      if (!canvas || !entry.el) return;

      const { obj, el } = entry;
      let rvfcId: number | null = null;
      let mstpReader: ReadableStreamDefaultReader<VideoFrame> | null = null;
      const hasRVFC = "requestVideoFrameCallback" in HTMLVideoElement.prototype;

      const onVideoArrived = async () => {
        // When hidden and Worker scheduler is running, skip video-driven renders
        // Must check isWorkerRunningRef to avoid freezing if worker fails to start
        const isHidden = document.visibilityState === 'hidden';
        if (isHidden && workerEnabled && isWorkerRunningRef.current) {
          // Phase 5: If OffscreenCanvas worker is active, feed it video frames
          if (RECORDING_FLAGS.OFFSCREEN_CANVAS && isOffscreenActiveRef.current && el) {
            const bitmap = await captureVideoFrame(el);
            if (bitmap) {
              const name = (obj as any).name;
              if (name === 'webcam-video') {
                updateOffscreenWebcamRef.current?.(bitmap);
              } else if (name === 'screen-video') {
                updateOffscreenScreenRef.current?.(bitmap);
              } else {
                bitmap.close();
              }
            }
          }
          // Worker will handle all renders at 30fps - just mark object dirty
          if (obj) (obj as any).dirty = true;
          return;
        }
        
        // Visible tab, worker disabled, or worker not running: trigger render normally
        markDirtyAndRender(canvas, obj);
      };

      // MediaStreamTrackProcessor frame signaling (if available)
      try {
        const ms = el.srcObject as MediaStream | null;
        const track = ms?.getVideoTracks?.()[0];
        if (track && 'MediaStreamTrackProcessor' in window) {
          const processor = new (window as any).MediaStreamTrackProcessor({ track });
          mstpReader = processor.readable.getReader();
          (async () => {
            for (;;) {
              const { value: frame, done } = await mstpReader!.read();
              if (done || !frame) break;
              onVideoArrived(); // Always trigger on frame arrival
              frame.close(); // critical: no pixel copies, no leaks
            }
          })();
        }
      } catch {}

      // RVFC loop (fallback for when MSTP is not available)
      if (hasRVFC) {
        const onRvfc = () => {
          onVideoArrived(); // Always trigger on frame arrival
          loop(); // keep listening
        };
        const loop = () => {
          rvfcId = (el as any).requestVideoFrameCallback(onRvfc);
        };
        loop();
      }

      // Event fallbacks (some environments fire these sooner)
      const onPlayableOnce = () => markDirtyAndRender(canvas, obj);
      const onProgress = () => markDirtyAndRender(canvas, obj);

      el.addEventListener("playing", onPlayableOnce);
      el.addEventListener("loadeddata", onPlayableOnce);
      el.addEventListener("timeupdate", onProgress);
      el.addEventListener("resize", onProgress);
      el.addEventListener("seeked", onProgress);

      // Gentle timed nudges in case events are delayed/throttled
      const t1 = setTimeout(() => markDirtyAndRender(canvas, obj), 150);
      const t2 = setTimeout(() => markDirtyAndRender(canvas, obj), 400);
      const t3 = setTimeout(() => markDirtyAndRender(canvas, obj), 900);

      entry.detach = () => {
        el.removeEventListener("playing", onPlayableOnce);
        el.removeEventListener("loadeddata", onPlayableOnce);
        el.removeEventListener("timeupdate", onProgress);
        el.removeEventListener("resize", onProgress);
        el.removeEventListener("seeked", onProgress);
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
        if (rvfcId != null && (el as any).cancelVideoFrameCallback) {
          try {
            (el as any).cancelVideoFrameCallback(rvfcId);
          } catch {}
        }
        // Clean up MSTP reader if set
        if (mstpReader) {
          try { mstpReader.cancel(); } catch {}
          mstpReader = null;
        }
      };
    },
    [markDirtyAndRender]
  );

  const registerVideoObject = useCallback(
    (object: fabric.Object) => {
      const element =
        (object as any).getElement?.() ??
        (object as any)._element ??
        null;

      const el =
        element && element.tagName === "VIDEO"
          ? (element as HTMLVideoElement)
          : null;

      const entry: Registered = { obj: object, el, detach: undefined };
      itemsRef.current.add(entry);
      // Also add to global registry so vimeoDirectRender can see all videos
      addToGlobalRegistry(entry);

      // If canvas already known, attach hooks now
      if (canvasRef.current && el) attachVideoHooks(entry);
    },
    [attachVideoHooks]
  );

  const unregisterVideoObject = useCallback((object: fabric.Object) => {
    // Snapshot to avoid stale-ref cleanup warnings
    const items = Array.from(itemsRef.current);
    items.forEach((entry) => {
      if (entry.obj === object) {
        try { entry.detach?.(); } catch {}
        itemsRef.current.delete(entry);
        // Also remove from global registry
        removeFromGlobalRegistry(entry);
      }
    });
  }, []);

  useEffect(() => {
    // Fabric render listeners with MSTG frame submission
    const onBefore = () => {
      isRenderingRef.current = true;
      renderStartTimeRef.current = performance.now();
    };
    
    const onAfter = () => {
      isRenderingRef.current = false;
      
      // Calculate render duration for MSTG frame budget
      const renderDurationMs = performance.now() - renderStartTimeRef.current;
      
      // Skip frame capture if direct render is active AND tab is hidden
      // This prevents double frame submission to MSTG during hidden-tab recording
      // When visible, always allow Fabric.js to handle frame capture
      if (directRenderActiveRef.current && document.visibilityState === 'hidden') {
        return;
      }
      
      // MSTG frame submission: check shouldCaptureFrame() dynamically each render
      // This prevents frame count drift and handles track state changes
      const shouldCapture = shouldCaptureFrameRef.current?.() ?? false;
      if (RECORDING_FLAGS.MSTG_ENABLED && shouldCapture && onFrameRenderedRef.current && canvasRef.current) {
        const domCanvas = (canvasRef.current as any).lowerCanvasEl as HTMLCanvasElement;
        if (domCanvas) {
          // Use current frame count (0-based), then increment for next frame
          const currentFrame = frameCountRef.current;
          frameCountRef.current++;
          // Call async writeFrame - catch any errors to prevent unhandled rejections
          // This is fire-and-forget since we can't block the render callback
          Promise.resolve(onFrameRenderedRef.current(domCanvas, currentFrame, renderDurationMs))
            .catch(() => {
              // Silently swallow errors - writeFrame handles its own error logging
              // This catch prevents "Uncaught runtime errors" from unhandled promise rejections
            });
        }
      }
      
      // Skip follow-up renders when hidden and worker is handling rendering
      // This prevents pendingRef from triggering extra renderAll() calls
      if (document.visibilityState === 'hidden' && workerEnabled && isWorkerRunningRef.current) {
        pendingRef.current = false;
        return;
      }
      
      if (pendingRef.current) {
        pendingRef.current = false;
        // one coalesced follow-up render (used when hidden)
        canvasRef.current?.renderAll();
        lastHiddenPaintRef.current = performance.now();
      }
    };

    const start = () => {
      const canvas =
        stageRef?.current && typeof stageRef.current?.getCanvas === "function"
          ? stageRef.current.getCanvas()
          : null;
      if (!canvas) return false;

      canvasRef.current = canvas;

      // Attach Fabric before/after render listeners
      canvas.on?.('before:render', onBefore);
      canvas.on?.('after:render', onAfter);

      // If canvas swapped, reattach hooks
      if (prevCanvasRef.current !== canvas) {
        const oldItems = Array.from(itemsRef.current);
        oldItems.forEach((entry) => {
          try { entry.detach?.(); } catch {}
        });
        const newItems = Array.from(itemsRef.current);
        newItems.forEach((entry) => {
          if (entry.el) attachVideoHooks(entry);
        });
        prevCanvasRef.current = canvas;
      }

      runningRef.current = true;

      // Start Worker-based frame scheduler if enabled
      // This provides unthrottled timing in hidden tabs
      if (workerEnabled && startWorkerRef.current) {
        startWorkerRef.current();
      }
      
      // One-time safety nudge after mount
      setTimeout(() => {
        const currentCanvas = canvasRef.current;
        if (currentCanvas) {
          currentCanvas.requestRenderAll();
        }
      }, 100);

      return true;
    };

    // Try immediately
    if (start()) {
      return () => {
        const items = Array.from(itemsRef.current);

        runningRef.current = false;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = null;

        // Stop Worker-based frame scheduler
        stopWorkerRef.current?.();

        // Clean up Fabric render listeners
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.off?.('before:render', onBefore);
          canvas.off?.('after:render', onAfter);
        }

        items.forEach((entry) => {
          try { entry.detach?.(); } catch {}
        });
        itemsRef.current.clear();
        canvasRef.current = null;
        prevCanvasRef.current = null;
      };
    }

    // Retry until Stage is ready
    const id = setInterval(() => {
      if (start()) clearInterval(id);
    }, 100);

    return () => {
      const items = Array.from(itemsRef.current);

      clearInterval(id);
      runningRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;

      // Stop Worker-based frame scheduler
      stopWorkerRef.current?.();

      // Clean up Fabric render listeners
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.off?.('before:render', onBefore);
        canvas.off?.('after:render', onAfter);
      }

      items.forEach((entry) => {
        try { entry.detach?.(); } catch {}
      });
      itemsRef.current.clear();
      canvasRef.current = null;
      prevCanvasRef.current = null;
    };
  }, [stageRef, attachVideoHooks, workerEnabled]);

  // Reset frame count for MSTG (call when recording starts)
  const resetFrameCount = useCallback(() => {
    frameCountRef.current = 0;
  }, []);

  // VISIBILITY BRIDGE: Handle RAF→Worker transition on tab visibility change
  // This prevents the 0.6s freeze when switching tabs during recording
  useEffect(() => {
    if (!RECORDING_FLAGS.VISIBILITY_BRIDGE || !workerEnabled) {
      return;
    }

    const handleVisibilityChange = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      // Always reset directRenderActiveRef on visibility change to prevent stuck state
      directRenderActiveRef.current = false;
      setDirectRenderActive(false);
      
      // If runningRef is true, we're actively recording - always bridge
      if (!runningRef.current) return;
      
      // Handle transition TO hidden state
      if (document.visibilityState === 'hidden') {
        // Ensure worker is started if not already running
        if (!isWorkerRunningRef.current && startWorkerRef.current) {
          startWorkerRef.current();
        }
        
        // Phase 5: Initialize and start OffscreenCanvas worker for hidden-tab rendering
        // Skip if worker has been disabled due to previous failure
        if (RECORDING_FLAGS.OFFSCREEN_CANVAS && offscreenEnabled && !isOffscreenActiveRef.current && !offscreenDisabledRef.current) {
          // Reset frame received flag for watchdog
          offscreenFrameReceivedRef.current = false;
          
          // Store canvas ref and set pending flag for onInitialized callback
          pendingOffscreenCanvasRef.current = canvas;
          pendingOffscreenStartRef.current = true;
          
          // Initialize with canvas dimensions and layout
          // The worker will send 'initialized' message, which triggers handleOffscreenInitialized
          // which then calls start() - no timed delay needed
          const canvasWidth = canvas.getWidth();
          const canvasHeight = canvas.getHeight();
          initOffscreenRef.current?.(canvasWidth, canvasHeight);
          
          // Send initial layout
          const layout = extractLayoutFromCanvas(canvas);
          updateOffscreenLayoutRef.current?.(layout);
        }
        
        // Mark all registered video objects as dirty so they get redrawn
        const items = Array.from(itemsRef.current);
        items.forEach((entry) => {
          if (entry.obj) {
            (entry.obj as any).dirty = true;
          }
        });
        
        // Force immediate synchronous render to capture first hidden frame
        canvas.renderAll();
        
        // Reset telemetry for new hidden session
        telemetryRef.current.windowStart = performance.now();
        telemetryRef.current.framesRendered = 0;
        telemetryRef.current.framesCaptured = 0;
        telemetryRef.current.totalRenderMs = 0;
        telemetryRef.current.maxRenderMs = 0;
        telemetryRef.current.droppedFrames = 0;
      } else {
        // Transitioning TO visible state
        telemetryRef.current.driver = 'fabric';
        
        // Stop OffscreenCanvas worker
        if (RECORDING_FLAGS.OFFSCREEN_CANVAS) {
          // Clear watchdog timer
          if (offscreenWatchdogRef.current) {
            clearTimeout(offscreenWatchdogRef.current);
            offscreenWatchdogRef.current = null;
          }
          
          if (isOffscreenActiveRef.current || isOffscreenRunningRef.current) {
            stopOffscreenRef.current?.();
            offscreenFrameReceivedRef.current = false;
            if (RECORDING_FLAGS.TELEMETRY_ENABLED) {
              console.log('[VisibilityBridge] Stopped OffscreenCanvas worker, resuming Fabric.js rendering');
            }
          }
        }
      }
      
      // Update timestamps to prevent duplicate renders
      lastHiddenPaintRef.current = performance.now();
      lastVisiblePaintRef.current = performance.now();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [workerEnabled, offscreenEnabled, extractLayoutFromCanvas]);

  return { registerVideoObject, unregisterVideoObject, resetFrameCount };
}
